import { Grid, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { User } from '@paintscout/api'
import { useSearchUsersQuery } from '@paintscout/api'
import type { DialogProps } from '@paintscout/ui'
import type { UserAssignments } from 'paintscout'
import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Spinner,
  Typography,
  useUser,
  UserTiles,
  useClientOptions,
  IconButton
} from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util/users'
import { getDeepObjectDiff } from '@paintscout/util/activity'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'

const useStyles = makeStyles((_theme: Theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))

export interface SelectStaffDialogProps extends DialogProps {
  currentUsers: UserAssignments
  isOptions?: boolean
  ownerId?: string
  onConfirm: (assignedUserIds: UserAssignments) => void
  onCancel: () => void
}

function SelectStaffDialog(props: SelectStaffDialogProps) {
  const {
    onCancel,
    onConfirm,
    currentUsers,
    ownerId,
    isOptions = false,
    loading: propLoading,
    open,
    ...baseDialogProps
  } = props
  const classes = useStyles()
  const { user: currentUser, isSuperadmin, isSales } = useUser()
  const { clientId } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const [newAssignedUsers, setNewAssignedUsers] = useState<UserAssignments>(currentUsers)
  const assignedUserIds = Object.keys(newAssignedUsers)
    .map((id) => (newAssignedUsers[id].assigned ? id : null))
    .filter(Boolean)

  const {
    loading: fetching,
    data,
    error
  } = useSearchUsersQuery({
    variables: {
      query: `${isSales ? '' : 'app_metadata.active:true'}${isSuperadmin ? ` AND app_metadata.companyId:"${clientId}"` : ''}`
    },
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
      enqueueSnackbar('Unable to retrieve Staff list', { variant: 'error' })
    }
  }, [error])

  const handleAssignUser = (user: User) => {
    const userId = removeAuth0Prefix(user.user_id)
    const userName = user.name
    const isSelected = assignedUserIds.indexOf(userId) !== -1

    // If selected already, unassign, else assign
    setNewAssignedUsers((newAssignedUsers) => {
      return {
        ...newAssignedUsers,
        [userId]: {
          assigned: !isSelected,
          name: userName
        }
      }
    })
  }

  const users = (data?.searchUsers?.rows ?? []).filter((user) => {
    const userId = removeAuth0Prefix(user.user_id)

    if (userId === removeAuth0Prefix(currentUser.user_id)) {
      return false
    }
    if (userId === ownerId) {
      return false
    }
    if (user.app_metadata?.roles.indexOf('crew') === -1 && user.app_metadata?.roles.indexOf('sales') === -1) {
      return false
    }
    return true
  })

  const queryLoading = fetching && !data?.searchUsers
  const dialogLoading = propLoading || queryLoading

  return (
    <Dialog open={open} maxWidth="md" {...baseDialogProps}>
      <DialogTitle
        loading={dialogLoading}
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={dialogLoading}>
            <CloseIcon />
          </IconButton>
        }
      >
        <Typography variant="h3">{isOptions ? `Select Default Asignees` : 'Share with Crew'}</Typography>
      </DialogTitle>
      <DialogContent>
        {queryLoading && <Spinner fullWidth fullHeight />}
        {!queryLoading && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="overline" gutterBottom>
                Crew and Sales
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                {isOptions
                  ? 'Selected users will be assigned to newly created Invoices by default.'
                  : users.length > 0
                    ? `Selected users can open this document`
                    : `There is no one else to share this document with`}
              </Typography>
              {users.length > 0 && (
                <UserTiles
                  users={users}
                  selectedUserIds={assignedUserIds.map((id) => addAuth0Prefix(id))}
                  onUserSelect={handleAssignUser}
                />
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          variant={'contained'}
          onClick={() => {
            onConfirm(newAssignedUsers)
          }}
          disabled={getDeepObjectDiff(newAssignedUsers, currentUsers).length === 0 || dialogLoading}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SelectStaffDialog
